import './i18n.js';
import 'ol/ol.css';
import * as Sentry from '@sentry/react';
import AppLayout from '@app/components/AppLayout';
import React, {useCallback} from 'react';
import ReactDOM from 'react-dom/client';
import initSentry from './sentry.js';
import store from './store.js';
import {AUTH_CLIENT_ID, AUTH_ISSUER} from './config.js';
import {Client as AuthClient} from '@apps-common/auth';
import {AuthProvider} from '@apps-common/auth-react';
import {BrowserRouter, useNavigate} from 'react-router-dom';
import {Provider, useDispatch, useSelector} from 'react-redux';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {Settings} from 'luxon';
import {asyncWithLDProvider, useLDClient} from 'launchdarkly-react-client-sdk';
import {getUserLabKeys} from './constants/index.js';
import {identify} from './analytics.js';
import {loadFonts} from '@apps-common/ui-theme';
import {setAuthToken} from '@apps-common/js-client/api/request';
import {setAuthToken as setGraneroAuthToken} from '@apps-common/geocorn-sdk/api';
import {setLabStatus} from '@actions/labs';

loadFonts();

Settings.defaultZone = 'utc';

// Polyfill for Windows
if (window !== undefined) {
  window.global = window;
}

// Ensure that all the blob support exists
if (Blob) {
  if (!Blob.prototype.text) {
    const reader = (blob, format) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = evt => {
          resolve(evt.target.result);
        };
        reader.onerror = () => {
          reject('Bad Blob');
        };
        if (format === 'text') {
          reader.readAsText(blob);
        } else {
          reader.readAsArrayBuffer(blob);
        }
      });
    };

    Blob.prototype.text = function () {
      return reader(this, 'text');
    };
    Blob.prototype.arrayBuffer = function () {
      return reader(this, 'arraybuffer');
    };
  }
}

// Setup Sentry
initSentry();

const authClient = new AuthClient({
  clientId: AUTH_CLIENT_ID,
  issuer: AUTH_ISSUER,
  redirectUri: `${window.location.origin}${import.meta.env.BASE_URL}`,
  postLogoutRedirectUri: `${window.location.origin}${import.meta.env.BASE_URL}`,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const SecureApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ldClient = useLDClient();

  const storedLabs = useSelector(state => state.labs);

  /**
   * @type {import('@apps-common/auth/client.js').ChangeListener}
   */
  const onAuthChange = useCallback(
    state => {
      setAuthToken(state.user?.accessToken || '');
      setGraneroAuthToken(state.user?.accessToken || '');

      if (state.user && !state.pending) {
        const {
          email,
          organization_id,
          organization_name,
          program_id,
          role_level,
          user_id,
        } = state.user.claims;

        identify({
          email,
          organization_id,
          organization_name,
          program_id,
          role_level,
          user_id,
        });

        Sentry.setUser({id: user_id, organization_id, program_id, email});

        // See https://launchdarkly.github.io/js-client-sdk/interfaces/LDMultiKindContext.html
        ldClient.identify({
          kind: 'multi',
          user: {
            key: `user-${user_id}`,
            email,
            role_level,
            user_id,
            _meta: {
              privateAttributes: ['email'], //https://docs.launchdarkly.com/sdk/features/private-attributes
            },
          },
          organization: {
            key: `organization-${organization_id}`,
            organization_id,
            organization_name,
            program_id,
            _meta: {
              privateAttributes: ['organization_name'],
            },
          },
        });

        // Users may have labs enabled in localStorage that they shouldn't have access to
        // This can occur if they enabled the lab in a different account on the same device, or if they edited their local store
        // This checks permissions when the user logs in, and disables labs that shouldn't be available
        const userLabs = getUserLabKeys(state.user);

        for (const labKey in storedLabs) {
          if (!userLabs.includes(labKey)) {
            dispatch(setLabStatus({labKey, enabled: false}));
          }
        }
      }
    },
    [dispatch, ldClient, storedLabs]
  );

  return (
    <AuthProvider
      client={authClient}
      navigate={navigate}
      onChange={onAuthChange}
      private
    >
      <QueryClientProvider client={queryClient}>
        <AppLayout />
        <ReactQueryDevtools />
      </QueryClientProvider>
    </AuthProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('pl-root'));

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: import.meta.env.VITE_LD_CLIENTSIDE_ID,
  });

  root.render(
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <Provider store={store}>
        <LDProvider>
          <SecureApp />
        </LDProvider>
      </Provider>
    </BrowserRouter>
  );
})();
